/**
 * Dashboard Page
 *
 * Main landing page after login. Shows role-specific content.
 *
 * Epic 5 (Verlof MVP, Story 5.1 — Rolgebaseerd dashboard, UX V7): herstructureerd
 * naar een rol-gesectioneerde compositie in vaste volgorde eigen → team →
 * beheer (AD-8), gebouwd bovenop het bestaande `hasRole`-JSX-patroon.
 *
 * - Medewerker-sectie: altijd zichtbaar (elke ingelogde gebruiker is óók
 *   medewerker). Bevat de nieuwe verlof-/rooster-widgets (Story 1.2/1.4) +
 *   bestaande, niet-verlof-gerelateerde content (`EmployeeDashboard`,
 *   "Taken Verkenner" — NFR-5: blijft functioneel intact, alleen herstijld).
 * - Team-sectie: additief, twee onafhankelijk gegateerde onderdelen die
 *   bewust niet worden samengevoegd (Story 5.2 AC #4) — "Mijn Team" is
 *   bestaande, ongewijzigde beoordelaar-functionaliteit (`hasRole`), de
 *   nieuwe teller/kalender-doorkijk is de AD-4-capability
 *   (`isTeamManagerOrHR`, incl. hr/admin — zie AuthContext.tsx voor de
 *   onderbouwing: het onderliggende `getApprovalQueue`/`getAttendanceCalendar`
 *   staat hr/admin org-breed toe, exact dezelfde AD-4-predicate).
 * - Beheer-sectie: `hasRole(['hr','admin'])`, met het bestaande
 *   organisatie-overzicht + de bestaande beheer-snelkoppelingen
 *   (Data Import/Systeeminstellingen zijn `admin`-only, ongewijzigd).
 */

import { useAuth } from '../features/auth';
import { Layout } from '../components/Layout';
import { EmployeeDashboard, HROrganizationDashboard } from '../features/dashboard';
import { EigenVerlofWidget } from '../features/dashboard/EigenVerlofWidget';
import { EigenRoosterWidget } from '../features/dashboard/EigenRoosterWidget';
import { EigenIndelingWidget } from '../features/dashboard/EigenIndelingWidget';
import { ManagerDashboardSection } from '../features/dashboard/ManagerDashboardSection';
import { Link } from 'react-router-dom';

export function DashboardPage() {
  const { hasRole, isTeamManagerOrHR } = useAuth();

  return (
    <Layout>
      <div data-testid="dashboard-page">
        {/* ── Medewerker-sectie (altijd) ─────────────────────────────── */}
        <section data-testid="dashboard-section-medewerker" className="mb-8 space-y-6">
          {/* Story C-5.1: het gereserveerde v2-haakje "eigen Indelingen" (verlof-
              spec V7) wordt hier gevuld — bestaande grid uitgebreid naar
              lg:grid-cols-3 i.p.v. een nieuwe dashboard-sectie. */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <EigenVerlofWidget />
            <EigenRoosterWidget />
            <EigenIndelingWidget />
          </div>

          <EmployeeDashboard />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <DashboardCard
              title="Taken Verkenner"
              description="Ontdek alle taken en criteria per niveau"
              linkTo="/taken-verkennen"
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              }
            />
          </div>
        </section>

        {/* ── Team-sectie (additief) ─────────────────────────────────── */}
        {(hasRole(['beoordelaar', 'hr', 'admin']) || isTeamManagerOrHR) && (
          <section data-testid="dashboard-section-team" className="mb-8 space-y-6">
            {isTeamManagerOrHR && <ManagerDashboardSection />}

            {hasRole(['beoordelaar', 'hr', 'admin']) && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <DashboardCard
                  title="Mijn Team"
                  description="Bekijk en beoordeel uw teamleden"
                  linkTo="/team"
                  icon={
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  }
                />
              </div>
            )}
          </section>
        )}

        {/* ── Beheer-sectie (HR/admin) ───────────────────────────────── */}
        {hasRole(['hr', 'admin']) && (
          <section data-testid="dashboard-section-beheer" className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-[#1E293B] mb-4">Organisatie Overzicht</h2>
              <HROrganizationDashboard />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <DashboardCard
                title="HR Overzicht"
                description="Bekijk alle beoordelingen en accordeer"
                linkTo="/hr"
                icon={
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                }
              />
              <DashboardCard
                title="Medewerkers Beheer"
                description="Beheer medewerkers en rollen"
                linkTo="/medewerkers"
                icon={
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                }
              />
              {hasRole('admin') && (
                <>
                  <DashboardCard
                    title="Data Import"
                    description="Importeer medewerkers en taken"
                    linkTo="/data-import"
                    icon={
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                    }
                  />
                  <DashboardCard
                    title="Systeeminstellingen"
                    description="Configureer het systeem"
                    linkTo="/instellingen"
                    icon={
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    }
                  />
                </>
              )}
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
}

interface DashboardCardProps {
  title: string;
  description: string;
  linkTo: string;
  icon: React.ReactNode;
}

function DashboardCard({ title, description, linkTo, icon }: DashboardCardProps) {
  return (
    <Link
      to={linkTo}
      className="block p-6 bg-white rounded-lg shadow-sm border border-[#E2E8F0] hover:shadow-md transition-shadow"
    >
      <div className="flex items-center space-x-4">
        <div className="flex-shrink-0 p-3 bg-[#2F5A7A]/10 rounded-lg text-[#2F5A7A]">
          {icon}
        </div>
        <div>
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          <p className="mt-1 text-sm text-gray-500">{description}</p>
        </div>
      </div>
    </Link>
  );
}
